import { supabase } from '@/integrations/supabase/client';
import { addDays, addHours, setHours, setMinutes } from 'date-fns';

interface ScheduleAllergenReminderParams {
  userId: string;
  babyId: string;
  foodName: string;
  allergenType: string;
  firstExposureDate: Date;
}

interface ScheduleMilestoneReminderParams {
  userId: string;
  babyId: string;
  babyName: string;
  ageInMonths: number;
}

/**
 * Schedule allergen re-exposure reminders after first introduction
 * Following the 3-day rule: remind on day 3, 5, 7, and weekly after
 */
export async function scheduleAllergenReminders({
  userId,
  babyId,
  foodName,
  allergenType,
  firstExposureDate,
}: ScheduleAllergenReminderParams) {
  const reminders = [
    {
      days: 3,
      title: '🥚 Allergen-påminnelse',
      body: `Husk å gi ${foodName} igjen i dag (dag 3)`,
    },
    {
      days: 5,
      title: '🥚 Allergen-påminnelse',
      body: `Tid for ny eksponering av ${foodName} (dag 5)`,
    },
    {
      days: 7,
      title: '🥚 Allergen-påminnelse',
      body: `Fortsett med ${foodName} for å opprettholde toleranse (dag 7)`,
    },
    {
      days: 14,
      title: '🥚 Ukentlig allergen-påminnelse',
      body: `Husk å inkludere ${foodName} i kostholdet denne uken`,
    },
  ];

  for (const reminder of reminders) {
    const scheduledFor = addDays(firstExposureDate, reminder.days);
    scheduledFor.setHours(10, 0, 0, 0); // Schedule for 10:00 AM

    await supabase.functions.invoke('send-notification', {
      body: {
        userId,
        babyId,
        title: reminder.title,
        body: reminder.body,
        type: 'allergen_reminder',
        scheduleFor: scheduledFor.toISOString(),
        metadata: {
          foodName,
          allergenType,
          reminderDay: reminder.days,
        },
      },
    });
  }
}

/**
 * Schedule milestone-based reminders when baby reaches new age milestones
 */
export async function scheduleMilestoneReminder({
  userId,
  babyId,
  babyName,
  ageInMonths,
}: ScheduleMilestoneReminderParams) {
  const milestones: Record<number, { title: string; body: string }> = {
    6: {
      title: '🎉 Ny milepæl!',
      body: `${babyName} er 6 måneder! Klar for mer tekstur og fingermat`,
    },
    8: {
      title: '🎉 Ny milepæl!',
      body: `${babyName} er 8 måneder! Prøv mer varierte teksturer og smaker`,
    },
    10: {
      title: '🎉 Ny milepæl!',
      body: `${babyName} er 10 måneder! Tid for mer familiemat og selvstendighet`,
    },
    12: {
      title: '🎉 Ett år!',
      body: `${babyName} er 1 år! Kan nå spise det meste av vanlig familiemat`,
    },
  };

  const milestone = milestones[ageInMonths];
  if (!milestone) return;

  // Schedule for next day at 9:00 AM
  const scheduledFor = addHours(new Date(), 24);
  scheduledFor.setHours(9, 0, 0, 0);

  await supabase.functions.invoke('send-notification', {
    body: {
      userId,
      babyId,
      title: milestone.title,
      body: milestone.body,
      type: 'milestone',
      scheduleFor: scheduledFor.toISOString(),
      metadata: {
        babyName,
        ageInMonths,
      },
    },
  });
}

/**
 * Schedule meal time reminder
 */
export async function scheduleMealTimeReminder(
  userId: string,
  babyId: string,
  babyName: string,
  mealType: 'breakfast' | 'lunch' | 'dinner',
  time: string // Format: "HH:MM"
) {
  const mealNames = {
    breakfast: 'Frokost',
    lunch: 'Lunsj',
    dinner: 'Middag',
  };

  const [hours, minutes] = time.split(':').map(Number);
  const scheduledFor = new Date();
  scheduledFor.setHours(hours, minutes, 0, 0);

  // If time has passed today, schedule for tomorrow
  if (scheduledFor < new Date()) {
    scheduledFor.setDate(scheduledFor.getDate() + 1);
  }

  await supabase.functions.invoke('send-notification', {
    body: {
      userId,
      babyId,
      title: `⏰ ${mealNames[mealType]}tid!`,
      body: `Det er tid for ${babyName}s ${mealNames[mealType].toLowerCase()}. Husk å logge!`,
      type: 'meal_time',
      scheduleFor: scheduledFor.toISOString(),
      metadata: {
        babyName,
        mealType,
      },
    },
  });
}

/**
 * Check and schedule inactivity reminder if no logs in last 3 days
 */
export async function checkAndScheduleInactivityReminder(
  userId: string,
  babyId: string,
  babyName: string,
  lastLogDate?: Date
) {
  if (!lastLogDate) return;

  const daysSinceLastLog = Math.floor(
    (new Date().getTime() - lastLogDate.getTime()) / (1000 * 60 * 60 * 24)
  );

  if (daysSinceLastLog >= 3) {
    // Schedule reminder for next day at 11:00 AM
    const scheduledFor = addDays(new Date(), 1);
    scheduledFor.setHours(11, 0, 0, 0);

    await supabase.functions.invoke('send-notification', {
      body: {
        userId,
        babyId,
        title: '👋 Vi savner deg!',
        body: `Det er ${daysSinceLastLog} dager siden du logget mat for ${babyName}. Husk å holde oversikten!`,
        type: 'inactivity',
        scheduleFor: scheduledFor.toISOString(),
        metadata: {
          babyName,
          daysSinceLastLog,
        },
      },
    });
  }
}

/**
 * Schedule monthly recap notification for the 1st of next month
 */
export async function scheduleMonthlyRecap(
  userId: string,
  babyId: string,
  babyName: string
) {
  const now = new Date();
  const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  nextMonth.setHours(9, 0, 0, 0);

  await supabase.functions.invoke('send-notification', {
    body: {
      userId,
      babyId,
      title: '📊 Månedsrapport klar!',
      body: `Se ${babyName}s matreise i ${now.toLocaleDateString('nb-NO', { month: 'long' })}. Statistikk, favoritter og nye achievements!`,
      type: 'monthly_recap',
      scheduleFor: nextMonth.toISOString(),
      metadata: {
        babyName,
        month: now.getMonth(),
        year: now.getFullYear(),
      },
    },
  });
}
